import React from "react";
import {
  type DisruptionFailure,
  DisruptionFailureEnum,
} from "@b2bportal/air-disruption-api";
import { ButtonTypeEnum } from "@b2bportal/core-ui";
import { ensureExhaustive, type RichError } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  RebookErrorModalActionType,
  type RebookErrorModalProps,
} from "./types";

export const useRebookErrorModal = ({
  error,
  open,
  onDismiss,
  actions = [],
}: RebookErrorModalProps) => {
  const { t } = useI18nContext();

  const message = React.useMemo(() => {
    switch (error.type) {
      case "axios":
        return {
          title: t("core-flights.rebook.errors.network.title"),
          subtitle: t("core-flights.rebook.errors.network.subtitle"),
          debug: error.error.message,
        };
      case "unknown":
        return {
          title: t("core-flights.rebook.errors.generic.title"),
          subtitle: t("core-flights.rebook.errors.generic.subtitle"),
          debug: String(error.error),
        };

      case "failure":
        return {
          title: t("core-flights.rebook.errors.generic.title"),
          subtitle: t("core-flights.rebook.errors.generic.subtitle"),
          debug:
            error.error.DisruptionFailure === DisruptionFailureEnum.Unknown
              ? error.error.message
              : `DisruptionFailure: ${error.error.DisruptionFailure}`,
        };
    }
  }, [t, error]);

  React.useEffect(() => {
    console.warn(`Rebooking error: ${message.debug}`);
  }, [message.debug]);

  const [dismissedError, setDismissedError] =
    React.useState<RichError<DisruptionFailure>>();

  const dismissAction = React.useCallback(() => {
    setDismissedError(error);
    onDismiss?.();
  }, [onDismiss, error]);

  const dismissCurrentError = React.useCallback(() => {
    setDismissedError(error);
  }, [error]);

  return React.useMemo(
    () => ({
      context: {
        message,
        isOpen: open && error !== dismissedError,
        actions: actions.map((action) => {
          switch (action.type) {
            case RebookErrorModalActionType.DISMISS:
              return {
                buttonType: action.style ?? ButtonTypeEnum.Secondary,
                className: action.className,
                label:
                  action.label ??
                  t("core-flights.rebook.errors.actions.dismiss"),
                action: () => {
                  dismissAction();
                  action.dismiss?.();
                },
              };

            case RebookErrorModalActionType.RETRY:
              return {
                buttonType: action.style ?? ButtonTypeEnum.Primary,
                className: action.className,
                label:
                  action.label ?? t("core-flights.rebook.errors.actions.retry"),
                action: () => {
                  action.retry();
                  if (action.shouldDismiss ?? true) {
                    dismissCurrentError();
                  }
                },
              };

            case RebookErrorModalActionType.CUSTOM:
              return {
                buttonType: action.style ?? ButtonTypeEnum.Primary,
                className: action.className,
                label: action.label,
                action: () => {
                  action.action();
                  if (action.shouldDismiss ?? true) {
                    dismissCurrentError();
                  }
                },
              };

            default:
              ensureExhaustive(action);
          }
        }),
      },
    }),
    [
      actions,
      dismissAction,
      dismissCurrentError,
      dismissedError,
      error,
      message,
      open,
      t,
    ]
  );
};
